import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react';
import { format, isSameDay, isSameMonth, isSameYear } from 'date-fns';
import es from 'date-fns/locale/es';
import useDashboardStore from '../../store/dashboardStore';

/**
 * DateRangeSelector - Selector de fechas conectado al dashboard store
 * Ya no recibe props, se conecta directamente a Zustand
 * 
 * @param {Object} props
 * @param {boolean} props.showLabel - Mostrar etiqueta de fecha a la derecha (default: true)
 * @param {string} props.className - Clases CSS adicionales
 */
export default function DateRangeSelector({ 
  showLabel = true,
  className = ''
}) {
  // Conectar al store global
  const { 
    selectedDate, 
    viewMode, 
    setSelectedDate, 
    setViewMode, 
    nextPeriod, 
    prevPeriod,
    resetDate
  } = useDashboardStore();

  // Verificar si ya estamos en la fecha actual según el viewMode
  const isCurrentPeriod = () => {
    const today = new Date();
    if (viewMode === 'daily') return isSameDay(selectedDate, today);
    if (viewMode === 'monthly') return isSameMonth(selectedDate, today);
    if (viewMode === 'yearly') return isSameYear(selectedDate, today);
    return false;
  };

  // Generar años para el selector (últimos 5 años + próximo)
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 6 }, (_, i) => currentYear - i + 1);

  // Valor formateado para inputs HTML
  const dateValue = format(selectedDate, 'yyyy-MM-dd');

  // Handler para cambio de tipo de filtro
  const handleTypeChange = (newType) => {
    setViewMode(newType);
  };

  // Handler para cambio de fecha desde inputs
  const handleDateChange = (newValue) => {
    setSelectedDate(newValue);
  };

  // Formatear la fecha para mostrar según el tipo
  const getDisplayLabel = () => {
    if (viewMode === 'daily') {
      return format(selectedDate, "EEEE d 'de' MMMM", { locale: es });
    } else if (viewMode === 'monthly') {
      return format(selectedDate, "MMMM 'de' yyyy", { locale: es });
    } else {
      return format(selectedDate, 'yyyy');
    }
  };

  return (
    <div className={`bg-white p-4 rounded-xl shadow-sm border border-gray-100 ${className}`}>
      <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
        
        {/* Selector de Tipo (Tabs) */}
        <div className="flex bg-gray-100 p-1 rounded-lg">
          {[
            { key: 'daily', label: 'Día' },
            { key: 'monthly', label: 'Mes' },
            { key: 'yearly', label: 'Año' }
          ].map(({ key, label }) => (
            <button
              key={key}
              type="button"
              onClick={() => handleTypeChange(key)}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                viewMode === key 
                  ? 'bg-white text-[#2A9D8F] shadow-sm' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Navegación y Selector de Fecha */}
        <div className="flex items-center gap-2">
          
          {/* Botón Hoy - Ancho fijo para evitar saltos de layout */}
          <div className="w-12 flex justify-end mr-1">
            <button
              type="button"
              onClick={resetDate}
              className={`px-2 py-1 text-sm font-medium text-[#2A9D8F] hover:bg-[#2A9D8F]/10 
                rounded-lg transition-all border border-[#2A9D8F]/30
                ${isCurrentPeriod() ? 'invisible' : 'visible'}`}
              title="Volver a la fecha actual"
            >
              Hoy
            </button>
          </div>

          {/* Botón Anterior */}
          <button
            type="button"
            onClick={prevPeriod}
            className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors"
            title="Anterior"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>

          {/* Selector de Fecha Dinámico */}
          <div className="relative min-w-[180px]">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Calendar className="h-4 w-4 text-gray-400" />
            </div>
            
            {viewMode === 'daily' && (
              <input
                type="date"
                value={dateValue}
                onChange={(e) => handleDateChange(e.target.value)}
                className="w-full pl-10 pr-3 py-2 border border-gray-200 rounded-lg text-sm
                  focus:ring-2 focus:ring-[#2A9D8F] focus:border-[#2A9D8F] transition-all"
              />
            )}

            {viewMode === 'monthly' && (
              <input
                type="month"
                value={dateValue.slice(0, 7)} // YYYY-MM
                onChange={(e) => handleDateChange(e.target.value + '-01')}
                className="w-full pl-10 pr-3 py-2 border border-gray-200 rounded-lg text-sm
                  focus:ring-2 focus:ring-[#2A9D8F] focus:border-[#2A9D8F] transition-all"
              />
            )}

            {viewMode === 'yearly' && (
              <select
                value={dateValue.slice(0, 4)} // YYYY
                onChange={(e) => handleDateChange(e.target.value + '-01-01')}
                className="w-full pl-10 pr-3 py-2 border border-gray-200 rounded-lg text-sm
                  focus:ring-2 focus:ring-[#2A9D8F] focus:border-[#2A9D8F] transition-all
                  appearance-none bg-white cursor-pointer"
              >
                {years.map((year) => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            )}
          </div>

          {/* Botón Siguiente */}
          <button
            type="button"
            onClick={nextPeriod}
            className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors"
            title="Siguiente"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>

        {/* Label de la fecha seleccionada (visible en pantallas grandes) */}
        {showLabel && (
          <div className="hidden lg:block text-sm text-gray-600 font-medium capitalize min-w-[200px] text-right">
            {getDisplayLabel()}
          </div>
        )}
      </div>
    </div>
  );
}
